import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Panel, Chip, Th, Td } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, ldnTime } from "@/lib/api";

const KINDS = [["levels_trade_opened", "Levels trade opened"], ["levels_trade_closed", "Levels trade closed"], ["kill_warning", "Kill switch warning"], ["kill_triggered", "Kill switch triggered"], ["levels_published", "Levels published"], ["oil_signal", "Oil Pairs signal"], ["oil_force_close", "Oil Pairs force close"], ["broker_lost", "MT5 connection lost"], ["broker_rate_limited", "MT5 allowance exhausted"]];

export function ProfileTab() {
  const { user, reload } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [n, setN] = useState(user.notify || { browser: true, sound: true, email: false, email_kinds: [] });
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const toggleKind = (k) => setN({ ...n, email_kinds: (n.email_kinds || []).includes(k) ? n.email_kinds.filter((x) => x !== k) : [...(n.email_kinds || []), k] });
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Panel title="Profile" testid="settings-profile">
        <div className="space-y-3">
          <div className="text-sm text-slate-400">{user.email} <Chip status={user.role === "superadmin" ? "ARMED" : "PAPER"} label={user.role === "superadmin" ? "super admin" : "user"} testid="settings-role-chip" /></div>
          <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-950" />
          <Button data-testid="profile-save-button" variant="secondary" onClick={() => run(() => api.put("/account/profile", { name }), "Profile saved")}>Save profile</Button>
        </div>
      </Panel>
      <Panel title="Password" testid="settings-password">
        <div className="space-y-3">
          <Input data-testid="password-current-input" type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="bg-slate-950" />
          <Input data-testid="password-new-input" type="password" placeholder="New password (8+ characters)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="bg-slate-950" />
          <Button data-testid="password-save-button" variant="secondary" onClick={() => run(async () => { await api.put("/account/password", pw); setPw({ current_password: "", new_password: "" }); }, "Password changed")}>Change password</Button>
        </div>
      </Panel>
      <Panel title="Notifications" testid="settings-notifications" className="lg:col-span-2">
        <div className="flex flex-wrap gap-6">
          {[["browser", "Browser notifications"], ["sound", "Sound alerts"], ["email", "Email me"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid={`notify-${k}-switch`} checked={!!n[k]} onCheckedChange={(v) => setN({ ...n, [k]: v })} />{l}</label>
          ))}
        </div>
        {n.email && (
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {KINDS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm text-slate-400"><Switch data-testid={`notify-kind-${k}`} checked={(n.email_kinds || []).length === 0 || n.email_kinds.includes(k)} onCheckedChange={() => toggleKind(k)} />{l}</label>)}
          </div>
        )}
        <Button data-testid="notify-save-button" className="mt-4" variant="secondary" onClick={() => run(() => api.put("/account/notifications", n), "Notification preferences saved")}>Save preferences</Button>
      </Panel>
    </div>
  );
}

export function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/audit").then((r) => setRows(r.data)); }, []);
  return (
    <Panel title="Audit log" testid="settings-audit">
      <div className="max-h-[520px] overflow-auto">
        <table className="w-full min-w-[640px]">
          <thead className="border-b border-slate-800"><tr><Th>When</Th><Th>Who</Th><Th>Action</Th><Th>Detail</Th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-800/60"><Td className="text-xs text-slate-400">{ldnTime(r.at)}</Td><Td className="text-xs">{r.user}</Td><Td className="num text-xs">{r.action}</Td><Td className="num max-w-[420px] truncate text-xs text-slate-400" title={JSON.stringify(r.detail)}>{JSON.stringify(r.detail)}</Td></tr>
          ))}</tbody>
        </table>
      </div>
    </Panel>
  );
}
